import { useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Pick a container the browser can actually record (Safari/iOS = mp4, others = webm)
function pickMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  const c = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/wav'];
  return c.find(m => MediaRecorder.isTypeSupported(m)) || '';
}
const extFor = (mime) => mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : mime.includes('wav') ? 'wav' : 'webm';

/**
 * useVoiceRecorder({ context, onText, onError })
 *  context: 'chat' | 'order' | 'reminder' | 'standup' | 'task' — tunes the Whisper
 *  vocabulary prompt + correction pass on the server.
 */
export default function useVoiceRecorder({ context = 'chat', onText, onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (recording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia) { onError?.('Microphone not supported on this browser'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const type = mr.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 500) { onError?.('Recording too short'); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, `voice-input.${extFor(type)}`);
          fd.append('context', context);
          const res = await axios.post(`${API}/voice/transcribe`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
          const text = (res.data?.text || '').trim();
          if (!text) { onError?.('Could not hear you — try again'); return; }
          onText?.(text, res.data);
        } catch (e) {
          onError?.(e.response?.data?.detail || 'Transcription failed');
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      onError?.('Microphone access denied');
    }
  };

  const stop = () => {
    if (!recording) return;
    try { recRef.current?.stop(); } catch {}
    setRecording(false);
  };

  return { recording, transcribing, start, stop, toggle: () => (recording ? stop() : start()) };
}
